import { useMemo } from "react";

import { useLocalization } from "@fluent/react";

import { useCurrentUser } from "../../../../components/contexts/CurrentUserContext";
import { useTransactionCategories } from "../hooks/useTransactionCategories";
import { useTransactionEnrichmentEnabled } from "../hooks/useTransactionEnrichmentEnabled";

import TransactionLedger from "./TransactionLedger";

import type {
  TransactionLedgerColumnId,
  TransactionLedgerFilterId,
} from "./TransactionLedger";

const LEDGER_PAGE_SIZE = 15;

const LEDGER_COLUMNS: TransactionLedgerColumnId[] = [
  "date",
  "description",
  "account",
  "category",
  "receipt",
  "amount",
  "balance",
];

const LEDGER_FILTERS: TransactionLedgerFilterId[] = [
  "saved-filters",
  "accounts",
  "date-range",
  "category",
  "method",
  "bai-codes",
  "type",
  "amount",
];

type LedgerTabProps = {
  replyTime: string;
};

const LedgerTab = ({ replyTime }: LedgerTabProps) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const transactionEnrichmentEnabled = useTransactionEnrichmentEnabled();
  // MX categories need no Narmi enrichment, so the tree's source can keep the filter.
  const { source: categorySource } = useTransactionCategories({
    enabled: !transactionEnrichmentEnabled,
  });
  const showCategoryFilter =
    transactionEnrichmentEnabled || categorySource === "mx";
  // BAI2 is a business reconciliation format. The gate is on who's logged in, not which
  // account is selected, so a consumer user never sees it even with access to a shared
  // business account.
  const filters = useMemo(
    () =>
      currentUser?.isBusiness()
        ? LEDGER_FILTERS
        : LEDGER_FILTERS.filter((id) => id !== "bai-codes"),
    [currentUser]
  );
  // Narmi slugs match only enrichment_detail rows, which stay empty with the flag off. The filter
  // stays registered so a selection saved under another source still narrows to nothing.
  const filtersShownOnlyWhenActive = useMemo<TransactionLedgerFilterId[]>(
    () => (showCategoryFilter ? [] : ["category"]),
    [showCategoryFilter]
  );
  return (
    <TransactionLedger
      columns={LEDGER_COLUMNS}
      pagination={{ enabled: true, pageSize: LEDGER_PAGE_SIZE }}
      receipts
      filters={filters}
      filtersShownOnlyWhenActive={filtersShownOnlyWhenActive}
      search="box"
      emptyState={
        <p className="fontColor--secondary">
          {l10n.getString("live-ledger-ledger-empty")}
        </p>
      }
      replyTime={replyTime}
    />
  );
};

export default LedgerTab;
